import { CURRENCY_INFO, DEFAULT_RATES, isValidRate, type CurrencyCode } from '@expenditure/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useCurrentUser } from '../auth/AuthProvider';
import { useToast } from '../components/Toast';
import { CurrencySelect, Field, PageHeader } from '../components/ui';
import { api } from '../lib/api';
import { errorMessage } from '../lib/format';
import { useInvalidateGroup } from '../lib/queries';

export function CreateGroupPage() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidateGroup();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>(user.defaultCurrency);
  // The one rate that matters most for this group: its own currency against
  // the dollar (for a dollar group: the riel). Others start from today's
  // rates and can be changed later in the group's settings.
  const rateCurrency: Exclude<CurrencyCode, 'USD'> = currency === 'USD' ? 'KHR' : currency;
  const latest = useQuery({ queryKey: ['rates'], queryFn: async () => (await api.rates()).rates, staleTime: 3_600_000 });
  const [rate, setRate] = useState<string>(DEFAULT_RATES[rateCurrency]);
  const [rateEdited, setRateEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  // Follow the picked currency (and today's rates once loaded) until the user types a rate.
  useEffect(() => {
    if (!rateEdited) setRate(latest.data?.[rateCurrency] ?? DEFAULT_RATES[rateCurrency]);
  }, [rateCurrency, latest.data, rateEdited]);

  const rateNumber = Number(rate);
  const rateValid =
    rateCurrency === 'KHR' ? Number.isInteger(rateNumber) && rateNumber >= 1000 && rateNumber <= 10000 : isValidRate(rate);
  const valid = name.trim().length > 0 && rateValid;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const detail = await api.createGroup({
        name: name.trim(),
        description: description.trim() || null,
        currency,
        ...(rateCurrency === 'KHR' ? { khrPerUsd: rateNumber } : { rates: { [rateCurrency]: rate } }),
      });
      await invalidate();
      void navigate(`/groups/${detail.group.id}`, { replace: true });
    } catch (error) {
      toast(errorMessage(error, t), 'error');
      setSubmitting(false);
    }
  };

  return (
    <form className="page page--no-tabbar app" onSubmit={(e) => void submit(e)} noValidate>
      <PageHeader title={t('createGroup.title')} />

      <Field label={t('createGroup.name')} error={touched && !name.trim() ? t('errors.required') : undefined}>
        <input
          className={`input ${touched && !name.trim() ? 'input--invalid' : ''}`}
          value={name}
          maxLength={100}
          autoFocus
          placeholder={t('createGroup.namePlaceholder')}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      <Field label={`${t('createGroup.description')} (${t('common.optional')})`}>
        <textarea
          className="input"
          value={description}
          maxLength={500}
          placeholder={t('createGroup.descriptionPlaceholder')}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      <div className="field">
        <span className="field-label">{t('createGroup.currency')}</span>
        <CurrencySelect
          value={currency}
          label={t('createGroup.currency')}
          onChange={(next) => {
            setRateEdited(false);
            setCurrency(next);
          }}
        />
        <span className="field-hint">{t('createGroup.currencyHint')}</span>
      </div>

      <Field
        label={t('createGroup.rate')}
        hint={t('createGroup.rateHint')}
        error={touched && !rateValid ? t('errors.invalidAmount') : undefined}
      >
        <div className="row">
          <span className="num muted nowrap">$1 =</span>
          <input
            className={`input num ${touched && !rateValid ? 'input--invalid' : ''}`}
            inputMode="decimal"
            aria-label={t('createGroup.rate')}
            value={rate}
            onChange={(e) => {
              setRateEdited(true);
              setRate(e.target.value.replace(',', '.').replace(rateCurrency === 'KHR' ? /\D/g : /[^\d.]/g, ''));
            }}
          />
          <span className="num muted">{CURRENCY_INFO[rateCurrency].symbol}</span>
        </div>
      </Field>

      <div className="sticky-footer">
        <button className="btn btn--primary btn--block" disabled={submitting}>
          {t('groups.create')}
        </button>
      </div>
    </form>
  );
}
